import Badge from '@cloudscape-design/components/badge'
import Box from '@cloudscape-design/components/box'
import Link from '@cloudscape-design/components/link'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import ExpandableSection from '@cloudscape-design/components/expandable-section'
import Header from '@cloudscape-design/components/header'
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs'
import Spinner from '@cloudscape-design/components/spinner'
import SplitPanel from '@cloudscape-design/components/split-panel'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Table from '@cloudscape-design/components/table'
import type { EngagementSignal, FitScore, Provider, ProviderStatus } from '@/lib/types'
import { formatDate } from '@/utils/format'

export function computeFitScore(p: Provider): FitScore {
  let score = 0
  const talking_points: string[] = []
  if (p.prescribing_volume_annual_usd !== null) {
    if (p.prescribing_volume_annual_usd > 100_000) {
      score += 4
      talking_points.push(`$${(p.prescribing_volume_annual_usd / 1000).toFixed(0)}k annual Medicare volume`)
    } else if (p.prescribing_volume_annual_usd > 50_000) {
      score += 2
      talking_points.push(`$${(p.prescribing_volume_annual_usd / 1000).toFixed(0)}k annual Medicare volume`)
    }
  }
  if (p.engagement_signals.length > 0) {
    score += 2
    talking_points.push(`${p.engagement_signals.length} prior engagement signal(s)`)
  }
  if (p.cms_rank_by_volume !== null && p.cms_rank_by_volume <= 100) {
    score += 1
    talking_points.push(`Ranked #${p.cms_rank_by_volume} by Medicare volume`)
  }
  const total = Math.min(score, 10)
  return {
    total,
    label: total >= 7 ? 'Strong Match' : total >= 4 ? 'Good Fit' : 'Low Priority',
    color: total >= 7 ? 'emerald' : total >= 4 ? 'amber' : 'zinc',
    talking_points,
  }
}

export function mapBadgeColor(color: string): 'blue' | 'green' | 'grey' | 'red' | 'severity-critical' | 'severity-high' | 'severity-medium' | 'severity-low' | 'severity-neutral' | undefined {
  switch (color) {
    case 'emerald': return 'green'
    case 'amber': return 'severity-medium'
    case 'zinc': return 'grey'
    default: return undefined
  }
}

export function computeTemperature(signals: EngagementSignal[]): 'hot' | 'warm' | 'cold' {
  const day = 24 * 60 * 60 * 1000
  const now = Date.now()
  if (signals.filter(s => now - new Date(s.date).getTime() < 7 * day).length >= 2) return 'hot'
  if (signals.filter(s => now - new Date(s.date).getTime() < 28 * day).length >= 1) return 'warm'
  return 'cold'
}

function statusBadgeColor(s: ProviderStatus): 'blue' | 'green' | 'grey' | 'red' | 'severity-low' {
  switch (s) {
    case 'new': return 'grey'
    case 'contacted': return 'blue'
    case 'engaged': return 'green'
    case 'referred': return 'severity-low'
    case 'inactive': return 'red'
  }
}

const STATUS_LABELS: Record<ProviderStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  engaged: 'Engaged',
  referred: 'Referred',
  inactive: 'Inactive',
}

const SIGNAL_LABELS: Partial<Record<EngagementSignal['signal_type'], string>> = {
  email_sent: 'Email sent',
  manual_contact: 'Manual contact',
  call: 'Call',
  meeting: 'Meeting',
  referral: 'Referral',
}

interface Props {
  provider: Provider
  emailLoading?: boolean
}

function fmt(usd: number | null | undefined): string {
  if (usd == null) return '—'
  return `$${(usd / 1000).toFixed(0)}k`
}

export default function ProviderDetailPanel({ provider, emailLoading = false }: Props) {
  const fit = computeFitScore(provider)
  const mv = provider.medicare_volume
  const hcpcs = provider.dme_hcpcs ?? []
  const signals = [...provider.engagement_signals].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  )

  return (
    <SplitPanel header={provider.provider_name}>
      <SpaceBetween size="l">

        <SpaceBetween size="xs">
          <Header variant="h3">Provider Profile</Header>
          <KeyValuePairs
            columns={2}
            items={[
              { label: 'Specialty', value: provider.specialty || '—' },
              { label: 'Practice', value: provider.practice_name || '—' },
              {
                label: 'Location',
                value: [
                  provider.address,
                  `${provider.city}, ${provider.state}${provider.zip ? ` ${provider.zip}` : ''}`,
                ].filter(Boolean).join(', '),
              },
              {
                label: 'NPI',
                value: (
                  <Link href={`https://npiregistry.cms.hhs.gov/provider-view/${provider.npi}`} external>
                    {provider.npi}
                  </Link>
                ),
              },
              ...(provider.entity_type != null
                ? [{ label: 'Type', value: provider.entity_type === 'I' ? 'Individual' : 'Organization' }]
                : []),
              ...(provider.years_in_practice != null
                ? [{ label: 'In Practice', value: `${provider.years_in_practice} yr${provider.years_in_practice !== 1 ? 's' : ''}` }]
                : []),
              ...(provider.ehr_system != null
                ? [{ label: 'EHR', value: provider.ehr_system }]
                : []),
              ...(provider.multi_location != null
                ? [{ label: 'Multi-location', value: provider.multi_location ? 'Yes' : 'No' }]
                : []),
            ]}
          />
        </SpaceBetween>

        <SpaceBetween size="xs">
          <Header variant="h3">Contact</Header>
          <KeyValuePairs
            columns={2}
            items={[
              {
                label: 'Email',
                value: emailLoading && !provider.email
                  ? <Spinner size="normal" />
                  : provider.email
                    ? (
                      <SpaceBetween size="xxs">
                        <Box>{provider.email}</Box>
                        {provider.email_last_checked && (
                          <Box variant="small" color="text-body-secondary">
                            Verified {formatDate(provider.email_last_checked)}
                          </Box>
                        )}
                      </SpaceBetween>
                    )
                    : '—',
              },
              ...(provider.phone != null
                ? [{ label: 'Phone', value: provider.phone }]
                : []),
              ...(provider.fax != null
                ? [{ label: 'Fax', value: provider.fax }]
                : []),
              ...(provider.website_url != null
                ? [{
                  label: 'Website',
                  value: (
                    <Link href={provider.website_url} external>
                      {provider.website_url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                    </Link>
                  ),
                }]
                : []),
              {
                label: 'Status',
                value: (
                  <Badge color={statusBadgeColor(provider.status)}>
                    {STATUS_LABELS[provider.status]}
                  </Badge>
                ),
              },
            ]}
          />
        </SpaceBetween>

        {(provider.medicare_type != null || provider.ruca_category != null || provider.total_benes != null || provider.has_rental_items != null) && (
          <SpaceBetween size="xs">
            <Header variant="h3">Patient Panel</Header>
            <KeyValuePairs
              columns={2}
              items={[
                ...(provider.medicare_type != null
                  ? [{ label: 'Medicare Type', value: provider.medicare_type.charAt(0).toUpperCase() + provider.medicare_type.slice(1) }]
                  : []),
                ...(provider.ruca_category != null
                  ? [{ label: 'Setting', value: provider.ruca_category }]
                  : []),
                ...(provider.total_benes != null
                  ? [{
                    label: 'Beneficiaries',
                    value: provider.bene_avg_age != null
                      ? `${provider.total_benes.toLocaleString()} · Avg age ${Math.round(provider.bene_avg_age)}`
                      : provider.total_benes.toLocaleString(),
                  }]
                  : []),
                ...(provider.has_rental_items != null
                  ? [{ label: 'Rents DME', value: provider.has_rental_items ? 'Yes' : 'No' }]
                  : []),
              ]}
            />
          </SpaceBetween>
        )}

        <ColumnLayout columns={4} borders="horizontal">
          <SpaceBetween size="xs">
            <Box variant="awsui-key-label">DME Volume</Box>
            <Box>
              {mv?.dme_usd != null
                ? fmt(mv.dme_usd)
                : provider.prescribing_volume_annual_usd != null
                  ? fmt(provider.prescribing_volume_annual_usd)
                  : provider.cms_no_data
                    ? 'No CMS data'
                    : '—'}
            </Box>
          </SpaceBetween>
          <SpaceBetween size="xs">
            <Box variant="awsui-key-label">Drug Volume</Box>
            <Box>{provider.cms_no_data ? '—' : fmt(mv?.drug_usd)}</Box>
          </SpaceBetween>
          <SpaceBetween size="xs">
            <Box variant="awsui-key-label">Total Medicare</Box>
            <Box>{provider.cms_no_data ? '—' : fmt(mv?.total_usd)}</Box>
          </SpaceBetween>
          <SpaceBetween size="xs">
            <Box variant="awsui-key-label">CMS Rank</Box>
            <Box>{provider.cms_rank_by_volume ? `#${provider.cms_rank_by_volume}` : '—'}</Box>
          </SpaceBetween>
        </ColumnLayout>

        <SpaceBetween size="xs">
          <Box variant="awsui-key-label">Fit Score</Box>
          <Badge color={mapBadgeColor(fit.color)}>{fit.label} ({fit.total}/10)</Badge>
          {fit.talking_points.map((point) => (
            <Box key={point} variant="small" color="text-body-secondary">• {point}</Box>
          ))}
        </SpaceBetween>

        <SpaceBetween size="xs">
          <Box variant="awsui-key-label">DME Billing Codes</Box>
          {hcpcs.length === 0 ? (
            provider.dme_product_mix.length > 0
              ? (
                <SpaceBetween size="xxs">
                  {[...provider.dme_product_mix]
                    .sort((a, b) => b.volume_usd - a.volume_usd)
                    .map(p => (
                      <Box key={p.product} variant="small">
                        {p.product}:{' '}
                        <Box variant="span" fontWeight="bold">{fmt(p.volume_usd)}</Box>
                      </Box>
                    ))}
                </SpaceBetween>
              )
              : <Box color="text-body-secondary">No product mix data</Box>
          ) : (
            <Table
              columnDefinitions={[
                { id: 'code', header: 'Code', cell: (item) => item.code, width: '15%' },
                { id: 'description', header: 'Description', cell: (item) => item.description },
                { id: 'volume', header: 'Volume', cell: (item) => fmt(item.volume_usd), width: '15%' },
                { id: 'claims', header: 'Claims', cell: (item) => item.claims.toLocaleString(), width: '12%' },
                { id: 'rental', header: 'Rental', cell: (item) => item.rental ? 'Yes' : '—', width: '10%' },
              ]}
              items={[...hcpcs].sort((a, b) => b.volume_usd - a.volume_usd)}
              variant="embedded"
            />
          )}
        </SpaceBetween>

        {signals.length > 0 && (
          <SpaceBetween size="xs">
            <Header variant="h3">Engagement History</Header>
            <SpaceBetween size="xxs">
              {signals.map((s, i) => (
                <Box key={`${s.date}-${i}`} variant="small">
                  <Box variant="span" color="text-body-secondary">{formatDate(s.date)}</Box>
                  {' · '}
                  <Box variant="span" fontWeight="bold">{SIGNAL_LABELS[s.signal_type] ?? s.signal_type}</Box>
                  {s.detail && (
                    <Box variant="span" color="text-body-secondary">{` — ${s.detail}`}</Box>
                  )}
                </Box>
              ))}
            </SpaceBetween>
          </SpaceBetween>
        )}

        {provider.outreach_email && (
          <ExpandableSection
            headerText={
              provider.outreach_email.sent_at
                ? `Outreach Email · sent ${formatDate(provider.outreach_email.sent_at)}`
                : 'Outreach Email'
            }
          >
            <SpaceBetween size="xs">
              <Box>
                <Box variant="span" fontWeight="bold">Subject: </Box>
                {provider.outreach_email.subject}
              </Box>
              <Box variant="small" color="text-body-secondary">{provider.outreach_email.body}</Box>
            </SpaceBetween>
          </ExpandableSection>
        )}

        {(provider.data_year || provider.cms_last_updated) && (
          <Box variant="small" color="text-body-secondary">
            CMS data: {provider.data_year ?? '—'} · Updated {provider.cms_last_updated ? formatDate(provider.cms_last_updated) : '—'}
          </Box>
        )}

      </SpaceBetween>
    </SplitPanel>
  )
}
